import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useDynamicSEO } from "../hooks/useDynamicSEO";
import { FAQ_ITEMS } from "./FAQ";

/* ============ Data ============ */
const HERO_PILLS = [
  { icon: "✅", text: "100% Free" },
  { icon: "🚫", text: "No watermarks" },
  { icon: "⚡", text: "Ready in 5 min" },
  { icon: "🔓", text: "No login needed" },
];

const WHY_FEATURES = [
  {
    icon: "🎨",
    title: "Beautiful Templates",
    body: "Choose from stunning, traditional wedding biodata designs.",
  },
  {
    icon: "✍️",
    title: "Easy to Customize",
    body: "Simple form to fill your details and personalize your biodata.",
  },
  {
    icon: "⬇️",
    title: "Instant Download",
    body: "Download your biodata in PDF format instantly.",
  },
  {
    icon: "🏆",
    title: "Professional Quality",
    body: "High-quality, print-ready biodata for sharing with families.",
  },
];

const STATS = [
  { emoji: "👨‍👩‍👧", num: "50,000+", label: "Happy Families" },
  { emoji: "🎨", num: "50+", label: "Beautiful Templates" },
  { emoji: "⭐", num: "4.9★", label: "Family Rating" },
  { emoji: "⚡", num: "5 Min", label: "To Create & Download" },
];

const VALUE_PROPS = [
  {
    kicker: "Culturally Accurate",
    title: "Made for Indian Families, by Indians",
    body:
      "Not a generic template tool. Every layout, field, and design choice is built around real Indian marriage biodata needs — from horoscope details to family background.",
  },
  {
    kicker: "Zero Data Storage",
    title: "Your Privacy is 100% Protected",
    body:
      "We never store your personal data on our servers. Your biodata is generated in your browser and downloaded directly — no account, no data leak, no risk.",
  },
  {
    kicker: "Instant PDF Download",
    title: "Ready to Share in Under 5 Minutes",
    body:
      "Fill the form, pick a template, download a pixel-perfect PDF — no design skills, no software installation, no login required. It just works.",
  },
  {
    kicker: "8+ Languages Supported",
    title: "Every Community, Every Language",
    body:
      "Hindu, Muslim, Christian, Sikh — Hindi, Marathi, Gujarati, Tamil, Telugu, Kannada. One platform that works for all 1.4 billion Indians.",
  },
];

const LANGUAGES = [
  { emoji: "🇮🇳", name: "English Biodata", native: "English" },
  { emoji: "🪔", name: "Hindi Biodata", native: "हिंदी" },
  { emoji: "🌺", name: "Marathi Biodata", native: "मराठी" },
  { emoji: "🦚", name: "Gujarati Biodata", native: "ગુજરાતી" },
  { emoji: "🌸", name: "Kannada Biodata", native: "ಕನ್ನಡ" },
  { emoji: "🌴", name: "Tamil Biodata", native: "தமிழ்" },
  { emoji: "🌻", name: "Telugu Biodata", native: "తెలుగు" },
  { emoji: "🌷", name: "Bengali Biodata", native: "বাংলা" },
];

const TEMPLATES = [
  {
    id: "classic",
    title: "Classic Elegance",
    body: "Traditional marriage biodata format for classic families.",
    accent: "linear-gradient(135deg, #fff5e6, #ffe4c4)",
    swatch: "#8b1e41",
    label: "Traditional",
  },
  {
    id: "modern",
    title: "Modern Romance",
    body: "Contemporary wedding biodata design with a romantic touch.",
    accent: "linear-gradient(135deg, #fff0f5, #ffe4ec)",
    swatch: "#e5254a",
    label: "Contemporary",
  },
  {
    id: "royal",
    title: "Traditional Charm",
    body: "Culturally rich biodata template perfect for Indian weddings.",
    accent: "linear-gradient(135deg, #fdf6e3, #f5deb3)",
    swatch: "#b8942f",
    label: "Royal",
  },
];

const RELIGIONS = [
  { emoji: "🕉️", name: "Hindu", tag: "Rashi, Gotra, Manglik" },
  { emoji: "☪️", name: "Muslim", tag: "Sect, Maslak, Family" },
  { emoji: "✝️", name: "Christian", tag: "Denomination, Church" },
  { emoji: "☬", name: "Sikh", tag: "Gurdwara, Family" },
  { emoji: "🪷", name: "Jain", tag: "Sect, Community" },
  { emoji: "☸️", name: "Buddhist", tag: "Tradition, Community" },
];

const TESTIMONIALS = [
  {
    quote:
      "InstaVivah is the best marriage biodata maker I have used! The free PDF download was instant and my family loved every design.",
    author: "Priya Sharma",
    role: "Mumbai · Bride",
    initial: "P",
  },
  {
    quote:
      "Stunning wedding biodata templates — super easy to use. I created my shaadi biodata online in just 5 minutes, no designer needed!",
    author: "Ananya Iyer",
    role: "Chennai · Bride's sister",
    initial: "A",
  },
  {
    quote:
      "Amazing customization for Hindu biodata format. I personalized every field and downloaded a print-ready PDF in seconds. Highly recommended!",
    author: "Rahul Kapoor",
    role: "Delhi · Groom",
    initial: "R",
  },
  {
    quote:
      "I needed a professional Muslim marriage biodata and InstaVivah delivered perfectly. It saved hours of formatting with a beautiful layout.",
    author: "Fatima Khan",
    role: "Hyderabad · Bride",
    initial: "F",
  },
  {
    quote:
      "Creating my matrimony biodata online was surprisingly simple. The step-by-step form made the whole process completely stress-free.",
    author: "Divya Nair",
    role: "Kochi · Bride",
    initial: "D",
  },
  {
    quote:
      "The modern wedding biodata design helped me make a great first impression on matrimonial sites. Best free biodata template website in India!",
    author: "Gurpreet Singh",
    role: "Chandigarh · Groom",
    initial: "G",
  },
];

const HERO_IMG =
  "https://images.unsplash.com/photo-1709979773998-2655b3dfa124?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";
// Composite banner assets (bride + groom + biodata mockup)
const BRIDE_IMG =
  "https://images.pexels.com/photos/28941551/pexels-photo-28941551.jpeg?auto=compress&cs=tinysrgb&w=600&q=80";
const GROOM_IMG =
  "https://images.pexels.com/photos/33049965/pexels-photo-33049965.jpeg?auto=compress&cs=tinysrgb&w=600&q=80";

export default function Landing() {
  const seo = useDynamicSEO("home");
  const orgLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "InstaVivah",
    url: typeof window !== "undefined" ? window.location.origin : "",
    logo: `${typeof window !== "undefined" ? window.location.origin : ""}/logo.png`,
    sameAs: [],
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "hello@instavivah.com",
        areaServed: "IN",
        availableLanguage: ["English", "Hindi"],
      },
    ],
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.slice(0, 5).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="wbb-page">
      <SEO
        title={
          seo?.title ||
          "Free Marriage Biodata Maker — Create PDF in 5 Min | InstaVivah"
        }
        path="/"
        canonicalPath={seo?.canonical_path || "/"}
        description={
          seo?.description ||
          "Free Indian marriage biodata in 5 minutes. Hindu, Muslim, Marathi, Tamil, Gujarati & more formats. No login, no watermarks. Download PDF free."
        }
        keywords={
          seo?.keywords ||
          "matrimonial biodata maker, marriage biodata online, free biodata pdf, hindu biodata, muslim biodata, christian biodata, sikh biodata, marathi biodata, tamil biodata, gujarati biodata"
        }
        ogTitle={seo?.og_title}
        ogDescription={seo?.og_description}
        ogImage={seo?.og_image}
        ogType={seo?.og_type}
        twitterCard={seo?.twitter_card}
        twitterTitle={seo?.twitter_title}
        twitterDescription={seo?.twitter_description}
        twitterImage={seo?.twitter_image}
        robots={seo?.robots}
        jsonLd={[orgLd, faqLd]}
      />
      <Navbar />

      {/* ===================== HERO ===================== */}
      <section className="wbb-hero" aria-labelledby="hero-title">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <div className="wbb-trust-pill" data-testid="hero-trust-pill">
                <span className="wbb-trust-dot" />
                Trusted by 50,000+ Indian Families
              </div>

              <h1 id="hero-title" className="wbb-hero-title" data-testid="hero-title">
                Create Your Perfect{" "}
                <span className="wbb-hero-accent">Marriage Biodata</span>
                <br />— Free
              </h1>

              <p className="wbb-hero-lead" data-testid="hero-lead">
                Beautiful, watermark-free PDF in under 5 minutes. No designer,
                no login, no cost. Made for{" "}
                <strong>every Indian community</strong> in 8+ languages.
              </p>

              <div className="wbb-hero-ctas" data-testid="hero-ctas">
                <Link
                  to="/biodata/build"
                  className="wbb-btn wbb-btn-primary"
                  data-testid="cta-build"
                >
                  Create My Free Biodata
                  <span className="wbb-arrow" aria-hidden="true">→</span>
                </Link>
                <Link
                  to="/templates"
                  className="wbb-btn wbb-btn-ghost"
                  data-testid="cta-templates"
                >
                  Browse Templates
                </Link>
              </div>

              <div className="wbb-hero-pills" data-testid="hero-pills">
                {HERO_PILLS.map((p, i) => (
                  <span key={i} className="wbb-hero-pill">
                    <span className="wbb-hero-pill-icon" aria-hidden="true">
                      {p.icon}
                    </span>
                    {p.text}
                  </span>
                ))}
              </div>

              <div className="wbb-social-proof" data-testid="hero-social-proof">
                <div className="wbb-avatar-stack" aria-hidden="true">
                  <span className="wbb-avatar">PS</span>
                  <span className="wbb-avatar">AR</span>
                  <span className="wbb-avatar">FK</span>
                  <span className="wbb-avatar">GS</span>
                </div>
                <div>
                  <div className="wbb-stars" aria-label="4.9 out of 5">
                    ★★★★★ <strong className="ms-1">4.9</strong>
                  </div>
                  <div className="wbb-social-sub">
                    50,000+ families · free forever
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="wbb-hero-visual" data-testid="hero-visual">
                {/* Composite banner: soft pink stage with 3 biodata sheets, bride + groom cutouts, and floating cards */}
                <div className="wbb-banner-stage" aria-hidden="true">
                  {/* Central biodata sheet (main) */}
                  <div className="wbb-banner-sheet wbb-banner-sheet-main">
                    <div className="wbb-sheet-crest">❦</div>
                    <div className="wbb-sheet-title">BIODATA</div>
                    <div className="wbb-sheet-sub">॥ श्री गणेशाय नमः ॥</div>
                    <div className="wbb-sheet-section">Personal Details</div>
                    <div className="wbb-sheet-row">
                      <span className="wbb-sheet-lbl">Full Name</span>
                      <span className="wbb-sheet-val">Priya Sharma</span>
                    </div>
                    <div className="wbb-sheet-row">
                      <span className="wbb-sheet-lbl">Date of Birth</span>
                      <span className="wbb-sheet-val">14 May 1995</span>
                    </div>
                    <div className="wbb-sheet-row">
                      <span className="wbb-sheet-lbl">Height</span>
                      <span className="wbb-sheet-val">5&apos; 4&quot;</span>
                    </div>
                    <div className="wbb-sheet-row">
                      <span className="wbb-sheet-lbl">Religion</span>
                      <span className="wbb-sheet-val">Hindu</span>
                    </div>
                    <div className="wbb-sheet-row">
                      <span className="wbb-sheet-lbl">Caste</span>
                      <span className="wbb-sheet-val">Brahmin</span>
                    </div>
                    <div className="wbb-sheet-row">
                      <span className="wbb-sheet-lbl">Gothra</span>
                      <span className="wbb-sheet-val">Kashyap</span>
                    </div>
                    <div className="wbb-sheet-section wbb-mt">Family Details</div>
                    <div className="wbb-sheet-row">
                      <span className="wbb-sheet-lbl">Father</span>
                      <span className="wbb-sheet-val">R. K. Sharma</span>
                    </div>
                    <div className="wbb-sheet-row">
                      <span className="wbb-sheet-lbl">Mother</span>
                      <span className="wbb-sheet-val">S. Sharma</span>
                    </div>
                  </div>

                  {/* Left mini biodata sheet with groom */}
                  <div className="wbb-banner-sheet wbb-banner-sheet-left">
                    <div className="wbb-mini-crest">Wedding Biodata</div>
                    <img
                      src={GROOM_IMG}
                      alt=""
                      className="wbb-mini-photo"
                      loading="lazy"
                    />
                    <div className="wbb-mini-lines">
                      <span /><span /><span style={{ width: "70%" }} />
                      <span /><span style={{ width: "82%" }} />
                    </div>
                  </div>

                  {/* Right mini biodata sheet with bride */}
                  <div className="wbb-banner-sheet wbb-banner-sheet-right">
                    <div className="wbb-mini-crest">Wedding Biodata</div>
                    <img
                      src={BRIDE_IMG}
                      alt=""
                      className="wbb-mini-photo"
                      loading="lazy"
                    />
                    <div className="wbb-mini-lines">
                      <span /><span style={{ width: "85%" }} />
                      <span /><span style={{ width: "60%" }} /><span />
                    </div>
                  </div>

                  {/* Decorative sparkles */}
                  <span className="wbb-spark wbb-spark-1">✦</span>
                  <span className="wbb-spark wbb-spark-2">✿</span>
                  <span className="wbb-spark wbb-spark-3">❦</span>
                </div>

                {/* Floating notification card */}
                <div className="wbb-float-card" data-testid="hero-float-card">
                  <div className="wbb-float-avatar">P</div>
                  <div>
                    <div className="wbb-float-title">
                      Priya downloaded her biodata
                    </div>
                    <div className="wbb-float-sub">
                      2 minutes ago · Free PDF
                    </div>
                  </div>
                </div>

                {/* Floating rating badge */}
                <div className="wbb-float-rating" data-testid="hero-float-rating">
                  <div className="wbb-float-rating-num">4.9</div>
                  <div className="wbb-float-rating-stars">★★★★★</div>
                  <div className="wbb-float-rating-sub">50,000+ families</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== WHY CHOOSE US ===================== */}
      <section className="wbb-section" aria-labelledby="why-title" data-testid="section-why">
        <div className="container">
          <div className="text-center mb-5">
            <div className="wbb-kicker mb-2">Why Choose Us?</div>
            <h2 id="why-title" className="wbb-h2">
              Everything you need to create the perfect wedding biodata
            </h2>
          </div>

          <div className="row g-4">
            {WHY_FEATURES.map((f, i) => (
              <div className="col-md-6 col-lg-3" key={i}>
                <article
                  className="wbb-feature-card"
                  data-testid={`why-feature-${i}`}
                >
                  <div className="wbb-feature-icon" aria-hidden="true">
                    {f.icon}
                  </div>
                  <h3 className="wbb-feature-title">{f.title}</h3>
                  <p className="wbb-feature-body">{f.body}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== BIG STORY + STATS ===================== */}
      <section
        className="wbb-section wbb-section-alt"
        aria-labelledby="story-title"
        data-testid="section-story"
      >
        <div className="container">
          <div className="text-center">
            <div className="wbb-kicker mb-2">
              Trusted by 50,000+ Indian Families
            </div>
            <h2 id="story-title" className="wbb-h2 wbb-h2-big">
              India&apos;s <span className="wbb-hero-accent">#1</span> Free
              Marriage Biodata Maker
            </h2>
            <p className="wbb-lead mx-auto" style={{ maxWidth: 780 }}>
              Create a <strong>professional marriage biodata online</strong> in
              under 5 minutes — completely free, no watermarks, no login, no
              designer needed. Trusted by families across India for{" "}
              <strong>Hindu, Muslim, Christian &amp; Sikh biodatas</strong> in
              8+ Indian languages.
            </p>
          </div>

          <div className="row g-4 mt-4 justify-content-center">
            <div className="col-lg-8">
              <div className="wbb-story-card">
                <div className="wbb-kicker mb-2">
                  Why We Built This — Our Story
                </div>
                <p>
                  We watched families spend hours struggling with outdated Word
                  templates, paying ₹500–₹2000 to graphic designers, or
                  settling for watermarked PDFs — just to create a simple{" "}
                  <strong>shaadi biodata</strong> for their child. It felt
                  unfair.
                </p>
                <p>
                  So we built <strong>InstaVivah</strong> — a completely free,
                  multilingual <strong>online marriage biodata maker</strong>{" "}
                  designed for every Indian community. From{" "}
                  <strong>Hindu vivah biodata</strong> to{" "}
                  <strong>Muslim nikah biodata</strong>, from{" "}
                  <strong>Marathi biodata</strong> to{" "}
                  <strong>Tamil biodata</strong>.
                </p>
                <ul className="wbb-check-list">
                  <li>100% Free — No hidden charges ever</li>
                  <li>No watermarks on your downloaded PDF</li>
                  <li>No account or login required</li>
                  <li>Your data never stored on our servers</li>
                </ul>
                <Link
                  to="/biodata/build"
                  className="wbb-btn wbb-btn-primary mt-3"
                  data-testid="cta-story"
                >
                  Create Your Free Biodata Now
                  <span className="wbb-arrow" aria-hidden="true">→</span>
                </Link>
                <div className="wbb-story-check">
                  ✓ No signup &nbsp;·&nbsp; ✓ Ready in 5 minutes &nbsp;·&nbsp; ✓ Free PDF download
                </div>
              </div>
            </div>
          </div>

          <div className="wbb-stats-row" data-testid="stats-row">
            {STATS.map((s, i) => (
              <div className="wbb-stat" key={i} data-testid={`stat-${i}`}>
                <div className="wbb-stat-emoji" aria-hidden="true">
                  {s.emoji}
                </div>
                <div className="wbb-stat-num">{s.num}</div>
                <div className="wbb-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== VALUE PROPS 2x2 ===================== */}
      <section
        className="wbb-section"
        aria-labelledby="values-title"
        data-testid="section-values"
      >
        <div className="container">
          <div className="text-center mb-5">
            <div className="wbb-kicker mb-2">What makes us different</div>
            <h2 id="values-title" className="wbb-h2">
              Built by Indians. Loved by every family.
            </h2>
          </div>
          <div className="row g-4">
            {VALUE_PROPS.map((v, i) => (
              <div className="col-md-6" key={i}>
                <article
                  className="wbb-value-card"
                  data-testid={`value-${i}`}
                >
                  <div className="wbb-value-kicker">{v.kicker}</div>
                  <h3 className="wbb-value-title">{v.title}</h3>
                  <p className="wbb-value-body">{v.body}</p>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== LANGUAGES ===================== */}
      <section
        className="wbb-section wbb-section-alt"
        aria-labelledby="lang-title"
        data-testid="section-languages"
      >
        <div className="container">
          <div className="text-center mb-5">
            <div className="wbb-kicker mb-2">Your Language</div>
            <h2 id="lang-title" className="wbb-h2">
              Create Marriage Biodata in{" "}
              <span className="wbb-hero-accent">Your Language</span>
            </h2>
            <p className="wbb-lead mx-auto" style={{ maxWidth: 680 }}>
              Begin your matrimonial journey with a biodata in your native
              language. Traditional format, beautiful templates.
            </p>
          </div>

          <div className="wbb-lang-grid">
            {LANGUAGES.map((l, i) => (
              <Link
                to="/biodata/build"
                className="wbb-lang-card"
                key={i}
                data-testid={`lang-${i}`}
              >
                <div className="wbb-lang-emoji" aria-hidden="true">
                  {l.emoji}
                </div>
                <div className="wbb-lang-name">{l.name}</div>
                <div className="wbb-lang-native">{l.native}</div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-4">
            <span className="text-muted small">Don&apos;t see your language?</span>{" "}
            <Link to="/biodata/build" className="wbb-link">
              Create in English →
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== TEMPLATES ===================== */}
      <section
        className="wbb-section"
        aria-labelledby="tpl-title"
        data-testid="section-templates"
      >
        <div className="container">
          <div className="text-center mb-5">
            <h2 id="tpl-title" className="wbb-h2">
              Beautiful Marriage Biodata Templates
            </h2>
            <p className="wbb-lead">
              Choose from our collection of elegant wedding biodata designs —
              free to download as PDF
            </p>
          </div>

          <div className="row g-4">
            {TEMPLATES.map((t, i) => (
              <div className="col-md-4" key={t.id}>
                <article
                  className="wbb-tpl-card"
                  data-testid={`tpl-card-${t.id}`}
                >
                  <div
                    className="wbb-tpl-preview"
                    style={{ background: t.accent }}
                  >
                    <div className="wbb-tpl-doc">
                      <div
                        className="wbb-tpl-doc-header"
                        style={{ color: t.swatch, borderColor: t.swatch }}
                      >
                        BIODATA
                      </div>
                      <div className="wbb-tpl-doc-lines">
                        <span />
                        <span />
                        <span />
                        <span style={{ width: "70%" }} />
                        <span />
                        <span style={{ width: "80%" }} />
                      </div>
                    </div>
                    <span
                      className="wbb-tpl-badge"
                      style={{ background: t.swatch }}
                    >
                      {t.label}
                    </span>
                  </div>
                  <div className="wbb-tpl-body">
                    <h3 className="wbb-tpl-title">{t.title}</h3>
                    <p className="wbb-tpl-desc">{t.body}</p>
                    <Link
                      to={`/biodata/build?template=${t.id}`}
                      className="wbb-btn wbb-btn-outline wbb-btn-block"
                      data-testid={`tpl-use-${t.id}`}
                    >
                      Use This Template
                      <span className="wbb-arrow" aria-hidden="true">→</span>
                    </Link>
                  </div>
                </article>
              </div>
            ))}
          </div>

          <div className="text-center mt-4">
            <Link to="/templates" className="wbb-link" data-testid="tpl-view-all">
              View all 50+ templates →
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== RELIGIONS ===================== */}
      <section
        className="wbb-section wbb-section-alt"
        aria-labelledby="rel-title"
        data-testid="section-religions"
      >
        <div className="container">
          <div className="text-center mb-5">
            <div className="wbb-kicker mb-2">Religion-Specific Biodata Templates</div>
            <h2 id="rel-title" className="wbb-h2">
              Free Marriage Biodata for{" "}
              <span className="wbb-hero-accent">Every Religion</span>
            </h2>
            <p className="wbb-lead mx-auto" style={{ maxWidth: 720 }}>
              Create a free <strong>Hindu biodata</strong>,{" "}
              <strong>Muslim biodata</strong>,{" "}
              <strong>Christian biodata</strong>,{" "}
              <strong>Sikh biodata</strong> or more — each with religion-specific
              fields like rashi, gotra, sect &amp; denomination. Instant PDF
              download, 100% free.
            </p>
          </div>

          <div className="wbb-rel-grid">
            {RELIGIONS.map((r, i) => (
              <Link
                to={`/biodata/build?religion=${encodeURIComponent(r.name)}`}
                className="wbb-rel-card"
                key={r.name}
                data-testid={`rel-${i}`}
              >
                <div className="wbb-rel-emoji" aria-hidden="true">
                  {r.emoji}
                </div>
                <div className="wbb-rel-name">{r.name} Biodata</div>
                <div className="wbb-rel-tag">{r.tag}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== TESTIMONIALS ===================== */}
      <section
        className="wbb-section"
        aria-labelledby="test-title"
        data-testid="section-testimonials"
      >
        <div className="container">
          <div className="text-center mb-5">
            <div className="wbb-kicker mb-2">Trusted Reviews</div>
            <h2 id="test-title" className="wbb-h2">
              What Families Say About{" "}
              <span className="wbb-hero-accent">InstaVivah</span>
            </h2>
            <p className="wbb-lead mx-auto" style={{ maxWidth: 720 }}>
              Thousands of families across India trust InstaVivah to create
              beautiful marriage biodata PDFs for Hindu, Muslim, Christian &amp;
              Sikh matrimony in English, Hindi, and Marathi.
            </p>
            <div className="wbb-rating-big" data-testid="test-rating">
              <span className="wbb-rating-big-num">5.0</span>
              <span className="wbb-rating-big-stars">★★★★★</span>
              <span className="wbb-rating-big-sub">
                Rated by <strong>10,000+</strong> families
              </span>
            </div>
          </div>

          <div className="row g-4">
            {TESTIMONIALS.map((t, i) => (
              <div className="col-md-6 col-lg-4" key={i}>
                <blockquote
                  className="wbb-testimonial"
                  data-testid={`testimonial-${i}`}
                >
                  <div className="wbb-test-stars">★★★★★</div>
                  <p className="wbb-test-quote">&ldquo;{t.quote}&rdquo;</p>
                  <div className="wbb-test-author">
                    <div className="wbb-test-avatar" aria-hidden="true">
                      {t.initial}
                    </div>
                    <div>
                      <div className="wbb-test-name">{t.author}</div>
                      <div className="wbb-test-role">{t.role}</div>
                    </div>
                  </div>
                </blockquote>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FAQ ===================== */}
      <section
        className="wbb-section wbb-section-alt"
        aria-labelledby="faq-title"
        data-testid="section-faq"
      >
        <div className="container">
          <div className="text-center mb-5">
            <div className="wbb-kicker mb-2">Frequently Asked Questions</div>
            <h2 id="faq-title" className="wbb-h2">
              Everything You Need to Know About{" "}
              <span className="wbb-hero-accent">Marriage Biodata</span>
            </h2>
            <p className="wbb-lead mx-auto" style={{ maxWidth: 680 }}>
              From Hindu, Muslim, Christian biodata to biodata in Hindi,
              Marathi, Tamil — find answers to all your questions.
            </p>
          </div>

          <div className="wbb-faq-list">
            {FAQ_ITEMS.slice(0, 8).map((f, i) => (
              <details
                className="wbb-faq-item"
                key={i}
                data-testid={`faq-${i}`}
              >
                <summary>
                  <span className="wbb-faq-num">{i + 1}</span>
                  <span>{f.q}</span>
                  <span className="wbb-faq-chev" aria-hidden="true">+</span>
                </summary>
                <div className="wbb-faq-body">{f.a}</div>
              </details>
            ))}
          </div>

          <div className="text-center mt-4">
            <Link to="/faq" className="wbb-link" data-testid="faq-view-all">
              View all questions →
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="wbb-final-cta" data-testid="section-final-cta">
        <div className="container text-center">
          <h2 className="wbb-h2 text-white">
            Ready to Create Your Marriage Biodata?
          </h2>
          <p className="wbb-lead text-white opacity-90 mb-4">
            Start building your perfect wedding biodata today — free, instant
            PDF download!
          </p>
          <Link
            to="/biodata/build"
            className="wbb-btn wbb-btn-white"
            data-testid="cta-final"
          >
            Create Your Biodata Now — It&apos;s Free
            <span className="wbb-arrow" aria-hidden="true">→</span>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
